import { createContext, useContext, useEffect, useState } from 'react';
import i18n from '../i18n';
import type { ReactNode } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import api from '@/services/api';
import { useSnackbar } from 'notistack';

type Lang = 'pt' | 'en' | 'es' | string;

interface LanguageContextType {
  language: Lang;
  setLanguage: (lang: Lang) => void;
  toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  // Inicializa com o idioma atual do i18n
  const [language, setLanguageState] = useState<Lang>(i18n.language);

  const setLanguage = async (lang: Lang) => {
    if (!lang) return;
    const newLang = lang.trim().toLowerCase();

    i18n
      .changeLanguage(newLang)
      .then(() => {
        localStorage.setItem('app_language', newLang);
      })
      .catch((err) => {
        console.error('Erro ao trocar idioma:', err);
      });

    try {
      const response = await api.patch(`/user-preferences?language=${newLang}`);
      if (response.status === 200) {
        enqueueSnackbar(t('contexts.language.successSave'), {
          variant: 'success',
        });
      }
    } catch {
      enqueueSnackbar(t('contexts.language.failedSave'), { variant: 'error' });
    }
  };

  const toggleLanguage = () => {
    const order = ['pt', 'en', 'es'];
    // Normaliza o idioma atual para garantir que o indexOf funcione
    const currentNormalized = language.split('-')[0];
    const idx = order.indexOf(currentNormalized);

    // Se não achar (ex: pt-BR), assume 0 (pt)
    const safeIdx = idx === -1 ? 0 : idx;
    const next = order[(safeIdx + 1) % order.length];

    setLanguage(next);
  };

  // Listener: Sempre que o i18n mudar (seja por código ou init), atualiza o React
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      setLanguageState(lng);
      // Garante que o localStorage esteja sincronizado mesmo se a mudança vier de fora
      localStorage.setItem('app_language', lng);
    };

    i18n.on('languageChanged', handleLanguageChanged);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage }}>
      <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};
